import { useState, useEffect, useCallback, useMemo } from 'react';
import { clientAPI } from '@/services/api';
import { useDebounce } from './useDebounce';

export const useClientPayments = (filters = {}) => {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });

  // Stabiliser les filtres pour éviter les boucles infinies
  const stableFilters = useMemo(() => filters, [
    filters.search,
    filters.status,
    filters.method,
    filters.start_date,
    filters.end_date,
  ]);

  // Debouncer les filtres pour éviter les requêtes trop fréquentes
  const debouncedFilters = useDebounce(stableFilters, 300);

  const fetchPayments = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      // Filtrer les paramètres vides ou "all"
      const cleanFilters = Object.fromEntries(
        Object.entries(debouncedFilters).filter(([key, value]) => 
          value !== '' && value !== 'all' && value != null
        )
      );
      
      const params = {
        page,
        ...cleanFilters,
      };
      
      const response = await clientAPI.getPayments(params);
      setPayments(response.data.results || response.data);
      setPagination({
        count: response.data.count || 0,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (err) {
      console.error('Erreur lors du chargement des paiements:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des paiements');
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);

  const fetchInvoices = useCallback(async (page = 1) => {
    try {
      setError(null);
      
      const cleanFilters = Object.fromEntries(
        Object.entries(debouncedFilters).filter(([key, value]) => 
          value !== '' && value !== 'all' && value != null
        )
      );
      
      const params = {
        page,
        ...cleanFilters,
      };
      
      const response = await clientAPI.getInvoices(params);
      setInvoices(response.data.results || response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des factures:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des factures');
    }
  }, [debouncedFilters]);

  const initiatePayment = useCallback(async (paymentId) => {
    try {
      const response = await clientAPI.initiatePayment(paymentId);
      // Recharger les paiements après initiation
      fetchPayments();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors de l\'initiation du paiement:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors de l\'initiation du paiement' 
      };
    }
  }, [fetchPayments]);

  useEffect(() => {
    fetchPayments();
    fetchInvoices();
  }, [fetchPayments, fetchInvoices]);

  return {
    payments,
    invoices,
    loading,
    error,
    pagination,
    fetchPayments,
    fetchInvoices,
    initiatePayment,
  };
};

